<template>
  <div ref="verification" class="verification">
    <form @submit.prevent="handleSubmit" class="verify-form">
      <div>
        <div class="header">
          <h1>
            <span class="inherit-font" v-translate>Join the</span>
            <br />
            <span class="inherit-font" v-translate>Grant Circle</span>
          </h1>
        </div>
        <div>
          <StepProgressBar />
        </div>
        <div>
          <h4 class="verify-eligibility-header" v-translate>Verify Eligibility</h4>
          <div class="unique-id-container">
            <md-field :style="{ display: 'flex', flexDirection: 'column' }">
              <md-input
                tabindex="1"
                :placeholder="$gettext('Enter your Unique ID')"
                required
                v-model="employeeId"
              ></md-input>
              <span v-if="this.errors" class="text-error-msg" v-html="errors" />
            </md-field>
            <!-- help icon -->
            <div class="help">
              <svg
                @click.prevent="clickedHelp"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
                width="24"
              >
                <path d="M0 0h24v24H0z" fill="none" />
                <path
                  d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"
                />
              </svg>
              <div ref="helpText" class="help-text" v-if="showHelp">
                {{
                  $language.current
                    | translateToCurrentLang(
                      organizationDetails.uniqueIdHelpCopy.es,
                      organizationDetails.uniqueIdHelpCopy.en,
                    )
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <md-button
        type="submit"
        tabindex="2"
        class="button"
        :class="{ 'disabled-button': disabled }"
        :disabled="disabled"
      >
        <span v-if="!loading" key="verify" v-translate>Verify my Id</span>
        <span v-else-if="loading">
          <LoadSpinner />
        </span>
      </md-button>
    </form>
    <span class="sign-in-text">
      <translate>Been here before?</translate>
      <router-link :to="{ name: 'Login' }" tabindex="3"
        >&nbsp;<span class="inherit-font" v-translate>Sign In</span></router-link
      >
    </span>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import LoadSpinner from '../components/LoadSpinner'
import EmployeeCode from '@/services/employeeCodes'
import StepProgressBar from '../components/stepper/StepProgressBar'
import { analyticsMixin } from '@/services/googleAnalytics'

export default {
  name: 'Verification',
  mixins: [analyticsMixin],
  components: {
    StepProgressBar,
    LoadSpinner,
  },
  data() {
    return {
      employeeId: '',
      verificationFailed: null,
      loading: false,
      errors: null,
      showHelp: false,
      lockout: false,
    }
  },
  methods: {
    clickedHelp() {
      // if showing, close and remove event listener
      // if not showing, open and add event listener
      if (!this.showHelp) {
        this.showHelp = true
        setTimeout(
          () => this.$refs.verification.addEventListener('click', this.helpTextCallBack),
          0,
        )
      }
    },
    helpTextCallBack() {
      this.showHelp = false
      this.$refs.verification.removeEventListener('click', this.helpTextCallBack)
    },
    handleSubmit() {
      this.verifyEmployee(this.employeeId)
    },
    async verifyEmployee(id) {
      this.loading = true
      this.errors = null
      this.trackClickedCTA()
      const employeeCode = await EmployeeCode.api.verifyEmployeeId(id).catch((err) => {
        this.eventsTracker('employee-id-submission', err.response.data.detail)
        if (err.response.data.attempts >= 5) {
          this.lockout = true
          return
        }
        try {
          // TODO: alter error_lookup to work as an actual mapping
          this.errors =
            this.error_lookup[err.response.data.detail](this.organizationDetails) +
            (err.response.data.detail == 'Invalid code provided.'
              ? `<br><br> ${this.$gettext('You have')} ${
                  5 - err.response.data.attempts
                } ${this.$gettext('more tries to enter a valid ID.')}`
              : '')
        } catch {
          this.errors = this.$gettext('There was a problem. Please try again later.')
        }
      })

      /* If verification didn't fail, route to account creation page, passing along employee code */
      this.loading = false
      if (!this.errors) {
        this.$router.push({
          name: 'Registration',
          params: { code: employeeCode },
        })
      }
    },
  },
  checkIfSubmitViaEnter(e) {
    if (e.key == 'Enter') {
      this.verifyEmployee(this.employeeId)
    }
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars', 'currentLanguage']),
    disabled() {
      if (this.lockout == true) {
        this.errors = this.error_lookup['No more attempts allowed'](this.organizationDetails)
        this.employeeId = ''
        return true
      }
      return !this.employeeId
    },

    error_lookup() {
      const translated = [
        this.$gettext('It appears you have already created an account.'),
        this.$gettext('Please sign in.'),
      ]
      const invalidCode = [
        this.$gettext('Sorry, you do not appear to be eligible to participate in the'),
        this.$gettext(
          'Grant Circle. If you have questions about eligibility, or believe you are eligible to participate, please email',
        ),
      ]

      const invalidAndInactiveGenerator = (org) =>
        `${invalidCode[0]} ${org.name}${this.currentLanguage == 'en_US' ? ' ' : ''}${
          invalidCode[1]
        } ${org.supportEmail}.`



      const exists = () => `${translated[0]} <a href="/login">${translated[1]}</a>`
      const inactive = invalidAndInactiveGenerator
      const invalid = invalidAndInactiveGenerator

      return {
        'Invalid code provided.': invalid,
        'El código que ingresó es inválido.': invalid,
        'An account has already been created with this unique ID.': exists,
        'Ya existe una cuenta con este ID único.': exists,
        'Inactive code provided.': inactive,
        'El código que ingresó está inactivo.': inactive,
        'No more attempts allowed': () =>
          this.$gettext(
            'You have reached the limit for invalid verification attempts.  You have been locked out for one hour.  Try again later.',
          ),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

.verification {
  @include screen-container();
}
.header {
  height: 4rem;
  margin-bottom: 3rem;
  margin-top: 3rem;
}
.verify-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.button {
  @include primary-button(var(--dark-brand-color));
  margin-bottom: 1rem;
}

.disabled-button {
  @include primary-button-disabled();
  margin-bottom: 1rem;
}
.sign-in-text {
  font-size: 0.8rem;
}
.verify-eligibility-header {
  display: flex;
  flex-flow: row;
  align-items: center;
}
.unique-id-container {
  margin-top: 1rem;
  display: flex;
  flex-flow: row;
  align-items: flex-start;

  .help {
    margin-left: 0.5rem;
    display: flex;
    flex-flow: row;
    align-items: center;
    padding-top: 1rem;
    margin-top: 0.25rem;
  }
  svg {
    fill: var(--dark-brand-color);
  }
  .help-text {
    position: absolute;
    font-size: 1rem;
    font-weight: 300;
    border-radius: 0.4rem;
    max-width: 12rem;
    padding: 1rem 0.8rem;
    box-shadow: 0 4px 8px 0 $mid-gray;
    background-color: $white;
    margin-left: -8rem;
    margin-top: 7rem;
    z-index: 101;

    /* prevent selection of text */
    -webkit-user-select: none; /* Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+/Edge */
    user-select: none; /* Standard */
  }
}
</style>
